import { useEffect, useRef, useState } from "react";
import { RefreshCcw, Check, RotateCcw, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { speak } from "@/lib/tts";
import { shuffle, randomFrom } from "@/lib/gameData";
import { SENTENCE_PUZZLES } from "@/lib/sentencePuzzleData";
import { GAME_FEEDBACK_MS, GAME_SUCCESS_MS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

function nextPuzzle(current) {
  let pick = randomFrom(SENTENCE_PUZZLES);
  let guard = 5;
  while (current && pick.id === current.id && guard-- > 0) pick = randomFrom(SENTENCE_PUZZLES);
  // Shuffle pool with attached original indices so duplicate words are addressable
  const pool = shuffle(pick.words.map((w, i) => ({ word: w, poolIndex: i })));
  return { puzzle: pick, pool };
}

export default function SentencePuzzle() {
  const [state, setState] = useState(() => nextPuzzle(null));
  const [slots, setSlots] = useState([]); // [{word, poolIndex}]
  const [usedPool, setUsedPool] = useState(new Set());
  const [result, setResult] = useState(null);
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const timersRef = useRef([]);
  const { award } = useAuth();

  const clearTimers = () => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  };
  useEffect(() => () => clearTimers(), []);

  useEffect(() => {
    setSlots([]);
    setUsedPool(new Set());
    setResult(null);
  }, [state]);

  const pickWord = (word, poolIndex) => {
    if (result || usedPool.has(poolIndex)) return;
    setSlots((s) => [...s, { word, poolIndex }]);
    setUsedPool((u) => new Set(u).add(poolIndex));
  };

  const removeSlot = (slotIdx) => {
    if (result === "correct") return;
    const removed = slots[slotIdx];
    if (!removed) return;
    setSlots((cur) => cur.filter((_, i) => i !== slotIdx));
    setUsedPool((u) => {
      const next = new Set(u);
      next.delete(removed.poolIndex);
      return next;
    });
    if (result === "wrong") setResult(null);
  };

  const reset = () => {
    if (result === "correct") return;
    setSlots([]);
    setUsedPool(new Set());
    setResult(null);
  };

  const check = () => {
    if (slots.length !== state.puzzle.words.length) {
      toast.error("Place all the words first.");
      return;
    }
    setAttempts((a) => a + 1);
    const built = slots.map((s) => s.word).join(" ");
    const target = state.puzzle.words.join(" ");
    if (built === target) {
      setResult("correct");
      setScore((s) => s + 1);
      award(10, "sentence");
      toast.success("Great sentence!");
      speak(built, { rate: 0.9 });
      const t = setTimeout(() => setState((cur) => nextPuzzle(cur.puzzle)), GAME_SUCCESS_MS);
      timersRef.current.push(t);
    } else {
      setResult("wrong");
      toast.error("Not quite — try again.");
      const t = setTimeout(() => setResult(null), GAME_FEEDBACK_MS);
      timersRef.current.push(t);
    }
  };

  const played = new Set(slots.map((s) => s.poolIndex));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div
          data-testid="sentence-score"
          className="inline-flex items-center gap-2 bg-violet-50 text-violet-700 px-4 py-2 rounded-full font-bold text-sm"
        >
          Score: {score} / {attempts}
        </div>
        <button
          data-testid="sentence-next"
          onClick={() => setState((cur) => nextPuzzle(cur.puzzle))}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 bg-slate-100 hover:bg-slate-200 font-bold text-sm text-slate-700 transition-colors"
        >
          <RefreshCcw className="w-4 h-4" /> New sentence
        </button>
      </div>

      <div className="bg-slate-50 rounded-3xl p-6 sm:p-8 mb-6">
        <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">
          Build the sentence in the correct order
        </p>
        <div
          data-testid="sentence-slots"
          className={`min-h-[80px] p-4 rounded-2xl border-2 border-dashed transition-all
            ${result === "correct" ? "border-emerald-400 bg-emerald-50" : ""}
            ${result === "wrong" ? "border-rose-400 bg-rose-50 phonx-shake" : ""}
            ${!result ? "border-slate-300 bg-white" : ""}
          `}
        >
          {slots.length === 0 ? (
            <p className="text-slate-400 text-center py-2">Tap words below to add them here…</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {slots.map((s, i) => (
                <button
                  key={`slot-${i}`}
                  data-testid={`sentence-slot-${i}`}
                  onClick={() => removeSlot(i)}
                  className={`rounded-xl px-3 py-1.5 font-medium text-lg transition-all
                    ${result === "correct"
                      ? "bg-emerald-500 text-white"
                      : "bg-white border-2 border-violet-200 text-slate-800 hover:border-rose-300"
                    }
                  `}
                >
                  {s.word}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <p className="text-center text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">
        Word bank
      </p>
      <div className="flex flex-wrap justify-center gap-2 mb-6">
        {state.pool.map((entry) => {
          const used = played.has(entry.poolIndex);
          return (
            <button
              key={`pool-${entry.poolIndex}`}
              data-testid={`sentence-pool-${entry.poolIndex}`}
              onClick={() => pickWord(entry.word, entry.poolIndex)}
              disabled={used || result === "correct"}
              className={`rounded-xl px-4 py-2 font-medium text-lg transition-all
                ${used
                  ? "bg-slate-100 text-slate-300 border-2 border-slate-100"
                  : "bg-white border-2 border-slate-200 text-slate-800 hover:border-violet-400 hover:text-violet-600 hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                }
              `}
            >
              {entry.word}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap justify-center gap-3">
        <button
          data-testid="sentence-hear"
          onClick={() => speak(state.puzzle.words.join(" "), { rate: 0.9 })}
          className="rounded-full px-5 py-3 bg-white border-2 border-slate-200 text-slate-700 font-bold hover:border-violet-400 transition-colors inline-flex items-center gap-2"
        >
          <Volume2 className="w-4 h-4" /> Hear the sentence
        </button>
        <button
          data-testid="sentence-reset"
          onClick={reset}
          className="rounded-full px-5 py-3 bg-white border-2 border-slate-200 text-slate-700 font-bold hover:border-slate-400 transition-colors inline-flex items-center gap-1.5"
        >
          <RotateCcw className="w-4 h-4" /> Clear
        </button>
        <button
          data-testid="sentence-check"
          onClick={check}
          disabled={slots.length !== state.puzzle.words.length || result === "correct"}
          className="rounded-full px-8 py-3 bg-violet-500 hover:bg-violet-600 disabled:bg-violet-200 text-white font-bold shadow-md hover:shadow-lg transition-all inline-flex items-center gap-2"
        >
          <Check className="w-5 h-5" /> Check
        </button>
      </div>
    </div>
  );
}
